import React, { useMemo, useContext, useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { Route, Switch } from 'react-router-dom';

import { Loader } from '../../components/Loader';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { DEFAULT_DISCOVER_URL } from '../../const';
import { CustomPageLayout } from '../../containers/CustomPageLayout';
import { DiscoverContext } from '../../context/DiscoverContext';
import { GET_HAS_NEWS } from '../../graphql/local';
import { INSIGHTS_SERP_ROUTE, SERP_ROUTES } from '../../routes';
import { LearnerInfo, Tenant } from '../../types';
import useSearchClient from '../../utils/hooks/useSearchClient';
import { LIST_VIEW } from './const';
import { CategoryPage } from './pages/CategoryPage';
import { ExpertPage } from './pages/ExpertPage';
import { ExpertsPage } from './pages/ExpertsPage';
import { LandingPage } from './pages/LandingPage';
import { SearchPage } from './pages/SearchPage';
import { TermPage } from './pages/TermPage';

interface DiscoverPageProps {
  tenant: Tenant;
  loadingTenant: boolean;
  userInfo?: LearnerInfo;
  userLoading: boolean;
}

export type GetHasNewsResponse = {
  hasNews?: boolean;
};

const DiscoverPage: React.FC<DiscoverPageProps> = ({
  tenant,
  loadingTenant,
  userInfo,
  userLoading,
}) => {
  const { indexName, loadingApiKey, searchClient } = useSearchClient();
  const { data: dataNews } = useQuery<GetHasNewsResponse>(GET_HAS_NEWS);
  const { setDiscoverView } = useContext(DiscoverContext);

  const routes = useMemo(
    () => (dataNews?.hasNews ? [...SERP_ROUTES, INSIGHTS_SERP_ROUTE] : SERP_ROUTES),
    [dataNews],
  );

  useEffect(() => {
    if (userInfo) {
      setDiscoverView(LIST_VIEW);
    }
  }, [userInfo, setDiscoverView]);

  if (loadingTenant) {
    return <Loader />;
  }

  return (
    <>
      <Box>
        <Switch>
          <Route
            path={`${DEFAULT_DISCOVER_URL}/glossary/:term`}
            render={(props) => (
              <TermPage
                userInfo={userInfo}
                tenant={tenant?.title}
                algoliaIndexName={indexName}
                searchClient={searchClient}
                tenantName={tenant?.name}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            path={routes}
            render={(props) => (
              <SearchPage
                algoliaIndexName={indexName}
                searchClient={searchClient}
                tenant={tenant}
                loadingKey={loadingApiKey}
                tenantName={tenant?.name}
                userLoading={userLoading}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            path={[
              '/expert/:expertSlug',
              '/experts/:expertSlug',
              `${DEFAULT_DISCOVER_URL}/experts/:expertSlug`,
            ]}
            render={(props) => (
              <ExpertPage
                algoliaIndexName={indexName}
                searchClient={searchClient}
                tenant={tenant}
                loadingKey={loadingApiKey}
                userLoading={userLoading}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            path={['/experts', `${DEFAULT_DISCOVER_URL}/experts`]}
            render={(props) => (
              <ExpertsPage
                searchClient={searchClient}
                tenant={tenant}
                loadingKey={loadingApiKey}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            path={[
              `${DEFAULT_DISCOVER_URL}/:category/:subcategory`,
              `${DEFAULT_DISCOVER_URL}/:category`,
            ]}
            render={(props) => (
              <CategoryPage
                algoliaIndexName={indexName}
                searchClient={searchClient}
                tenant={tenant}
                loadingKey={loadingApiKey}
                userLoading={userLoading}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          {!!userInfo && (
            <ProtectedRoute
              exact={false}
              userLoading={userLoading}
              userInfo={userInfo}
              path={`${DEFAULT_DISCOVER_URL}`}
              render={(props) => (
                <LandingPage tenant={tenant} fallback={<Loader />} userInfo={userInfo} {...props} />
              )}
            />
          )}
          <Route
            path={DEFAULT_DISCOVER_URL}
            render={(props) => <CustomPageLayout {...props} userInfo={userInfo} tenant={tenant} />}
          />
        </Switch>
      </Box>
    </>
  );
};

export default DiscoverPage;
